import Axios, {
    AxiosError,
    AxiosInstance,
    AxiosProxyConfig,
    AxiosResponse,
    InternalAxiosRequestConfig,
} from 'axios';
import { buildAxiosFetch } from 'axios-fetch';
import { attachTokenUpdateCallback, getToken, isTokenValid, setToken } from './tokenStorage';
import TunzNetworkError, { NetworkErrorStatus } from './TunzNetworkError';
import { v4 } from 'uuid';

let proxyConfig: AxiosProxyConfig | false = false;

export type TunzWsError = {
    status: number;
    code: number;
    msg: string;
};
type TraceId = string;

const isTunzWsError = (obj: any): obj is TunzWsError =>
    obj !== undefined &&
    obj.msg !== undefined &&
    obj.status !== undefined &&
    obj.code !== undefined;

export const setProxyConfig = (c: AxiosProxyConfig | false): void => {
    proxyConfig = c;
};
export const HEADER_TRACE_ID = 'X-Trace-Id';

const captureToken = (response: AxiosResponse): AxiosResponse => {
    if (response.headers) {
        const { token } = response.headers;
        if (token) {
            setToken(token);
        }
    } else {
        response.headers = {};
    }
    return response;
};

const insertToken = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = getToken();

    const headers = config.headers || {};
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    return { ...config, headers, proxy: proxyConfig };
};

const insertTraceId = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const traceId = v4();
    const headers = config.headers || {};
    headers[HEADER_TRACE_ID] = traceId;
    return { ...config, headers, proxy: proxyConfig };
};

const customErrorHandling = (error: AxiosError): Promise<AxiosResponse> => {
    const { response, config } = error;
    if (response) {
        const traceId = error.config?.headers?.[HEADER_TRACE_ID].toString();

        if (response.status === 401) {
            throw newUnauthorizedError(
                `Not authorized [trace.id: ${traceId}]: ${response.data}`,
                error,
                traceId,
            );
        }
        if (response.status === 403) {
            throw newForbiddenError(
                `Forbidden [trace.id: ${traceId}]: ${response.data}`,
                error,
                traceId,
            );
        }
        if (response.status === 404) {
            throw newResourceNotFoundError(config?.url, error, traceId);
        }
        if (isTunzWsError(response.data)) {
            const twe = response.data;

            throw new Error(
                `${twe.msg} - [status: ${twe.status}] - [code: ${twe.code}] - [trace.id: ${traceId}]`,
            );
        }
    }

    return Promise.reject(error);
};

function newUnauthorizedError(
    message: string,
    error: AxiosError,
    traceId?: TraceId,
): TunzNetworkError {
    return new TunzNetworkError(NetworkErrorStatus.UNAUTHORIZED, message, error, traceId);
}

function newForbiddenError(
    message: string,
    error: AxiosError,
    traceId?: TraceId,
): TunzNetworkError {
    return new TunzNetworkError(NetworkErrorStatus.FORBIDDEN, message, error, traceId);
}

function newResourceNotFoundError(
    resource: string | undefined,
    error: AxiosError,
    traceId?: TraceId,
): TunzNetworkError {
    let message = `Resource not found [trace.id: ${traceId}]: ${resource}`;

    if (error.response && isTunzWsError(error.response.data)) {
        const twe = error.response.data as TunzWsError;
        message = `${twe.msg} - [status: ${twe.status}] - [code: ${twe.code}] - [trace.id: ${traceId}]`;
    }

    return new TunzNetworkError(NetworkErrorStatus.NOT_FOUND, message, error, traceId);
}

Axios.defaults.withCredentials = true;
if (window.IFS_ENV && window.IFS_ENV.API_URL) {
    Axios.defaults.baseURL = window.IFS_ENV.API_URL;
}
Axios.interceptors.response.use(captureToken, customErrorHandling);
Axios.interceptors.request.use(insertToken);
Axios.interceptors.request.use(insertTraceId);

window.fetch = buildAxiosFetch(Axios);

export interface IHttpClient extends AxiosInstance {
    isLoggedIn: boolean;
}

const HttpClient: IHttpClient = Object.assign(Axios, { isLoggedIn: false });

attachTokenUpdateCallback(token => {
    if (token && isTokenValid(token)) {
        HttpClient.isLoggedIn = true;
    } else {
        HttpClient.isLoggedIn = false;
    }
});

export default HttpClient;
